import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import API_UTIL from '../../services/AuthIntereptor';
import { Button, Icons, Input, ModalHeader, ModalShell } from '../ui';

const emptyForm = {
  org_name: '',
  role: 'creator',
  social_media: {
    instagram: '',
    youtube: '',
    facebook: '',
  },
};

const normaliseUrl = (value) => value?.trim() || '';

const SocialInput = ({ icon: Icon, label, name, value, placeholder, onChange }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1.5">{label}</label>
    <div className="flex items-center border border-gray-200 rounded-lg overflow-hidden focus-within:border-brand-500 focus-within:ring-2 focus-within:ring-brand-500/30">
      <span className="px-3 py-2.5 bg-gray-50 border-r border-gray-200 text-slate-400">
        <Icon className="w-4 h-4" />
      </span>
      <input
        type="url"
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className="flex-1 min-w-0 bg-white text-slate-900 placeholder-slate-400 text-sm px-3 py-2.5 focus:outline-none"
      />
    </div>
  </div>
);

const EditPortfolioModal = ({ open, onClose, userPhoneNumber, onSaved }) => {
  const [formData, setFormData] = useState(emptyForm);
  const [userName, setUserName] = useState('');
  const [bannerFile, setBannerFile] = useState(null);
  const [fileName, setFileName] = useState('');
  const [dragging, setDragging] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !userPhoneNumber) return;

    let cancelled = false;

    const loadPortfolio = async () => {
      setLoading(true);
      setBannerFile(null);
      setFileName('');

      try {
        const response = await API_UTIL.get(`/fetchUserDetails/${userPhoneNumber}`);
        const details = response.data?.data || {};

        if (cancelled) return;

        const existingUserName = details.user_name || '';
        const hasPublicName = existingUserName && existingUserName !== userPhoneNumber;

        setUserName(existingUserName);
        setFormData({
          org_name: details.org_name || (hasPublicName ? existingUserName : ''),
          role: details.role || 'creator',
          social_media: {
            instagram: details.social_media?.instagram || '',
            youtube: details.social_media?.youtube || '',
            facebook: details.social_media?.facebook || '',
          },
        });
      } catch (error) {
        if (!cancelled) {
          console.error('Error loading portfolio details:', error);
          toast.error('Failed to load portfolio details');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadPortfolio();

    return () => {
      cancelled = true;
    };
  }, [open, userPhoneNumber]);

  const setSocialValue = (event) => {
    const { name, value } = event.target;
    setFormData((current) => ({
      ...current,
      social_media: {
        ...current.social_media,
        [name]: value,
      },
    }));
  };

  const selectBannerFile = (file) => {
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please choose an image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Banner image must be under 5 MB');
      return;
    }

    setBannerFile(file);
    setFileName(file.name);
  };

  const handleSave = async (event) => {
    event.preventDefault();

    const brandName = formData.org_name.trim();
    if (!brandName) {
      toast.error('Brand Name is required');
      return;
    }

    setSaving(true);

    try {
      const hasExistingUserName = userName && userName !== userPhoneNumber;
      const requestData = {
        user_phone_number: userPhoneNumber,
        org_name: brandName,
        role: formData.role || 'creator',
        social_media: {
          instagram: normaliseUrl(formData.social_media.instagram),
          youtube: normaliseUrl(formData.social_media.youtube),
          facebook: normaliseUrl(formData.social_media.facebook),
        },
      };

      if (!hasExistingUserName) {
        requestData.user_name = brandName;
      }

      const response = await API_UTIL.post('/updatePortfolioDetails', requestData);
      const savedUser = response.data?.data || {};
      const savedUserName = savedUser.user_name || userName || brandName.toLowerCase().replace(/\s+/g, '');

      if (bannerFile) {
        const bannerData = new FormData();
        bannerData.append('bannerImage', bannerFile);
        bannerData.append('userName', savedUserName);

        await API_UTIL.post('/updateBannerImage', bannerData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }

      setUserName(savedUserName);
      onSaved?.(savedUser);
      toast.success('Portfolio updated successfully');
      onClose();
    } catch (error) {
      console.error('Error saving portfolio details:', error);
      toast.error('Failed to update portfolio');
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalShell open={open} onClose={saving ? undefined : onClose} maxWidth="max-w-lg">
      <ModalHeader
        title="Edit Portfolio"
        subtitle="Update your public profile shown on event pages."
        onClose={saving ? undefined : onClose}
      />

      <form onSubmit={handleSave} className="p-6 space-y-5">
        <Input
          label="Brand Name"
          type="text"
          value={formData.org_name}
          onChange={(event) => setFormData((current) => ({ ...current, org_name: event.target.value }))}
          placeholder="e.g. The Stevie Awards"
          disabled={loading || saving}
          required
        />

        <SocialInput
          icon={Icons.Instagram}
          label="Instagram URL"
          name="instagram"
          value={formData.social_media.instagram}
          onChange={setSocialValue}
          placeholder="https://instagram.com/yourhandle"
        />

        <SocialInput
          icon={Icons.Youtube}
          label="YouTube URL"
          name="youtube"
          value={formData.social_media.youtube}
          onChange={setSocialValue}
          placeholder="https://youtube.com/@yourchannel"
        />

        <SocialInput
          icon={Icons.Facebook}
          label="Facebook URL"
          name="facebook"
          value={formData.social_media.facebook}
          onChange={setSocialValue}
          placeholder="https://facebook.com/yourpage"
        />

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">Upload Banner Image</label>
          <label
            onDragOver={(event) => {
              event.preventDefault();
              setDragging(true);
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={(event) => {
              event.preventDefault();
              setDragging(false);
              selectBannerFile(event.dataTransfer.files?.[0]);
            }}
            className={`flex flex-col items-center justify-center gap-2 w-full rounded-xl border-2 border-dashed px-6 py-8 cursor-pointer transition ${
              dragging ? 'border-brand-500 bg-brand-50/40' : 'border-gray-300 bg-gray-50 hover:border-brand-400 hover:bg-brand-50/20'
            }`}
          >
            <Icons.Upload className={`w-6 h-6 ${dragging ? 'text-brand-600' : 'text-slate-400'}`} />
            {fileName ? (
              <span className="text-sm font-medium text-brand-600 text-center break-all">{fileName}</span>
            ) : (
              <>
                <span className="text-sm font-medium text-slate-700 text-center">
                  Choose file <span className="text-slate-400 font-normal">or drag and drop</span>
                </span>
                <span className="text-[11px] text-slate-400 text-center">PNG, JPG up to 5 MB. Recommended 1920 x 480px</span>
              </>
            )}
            <input
              type="file"
              accept="image/*"
              className="hidden"
              disabled={loading || saving}
              onChange={(event) => selectBannerFile(event.target.files?.[0])}
            />
          </label>
        </div>

        <div className="pt-2 flex items-center gap-3">
          <Button type="submit" className="px-6" disabled={loading || saving}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
          <Button type="button" variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
        </div>
      </form>
    </ModalShell>
  );
};

export default EditPortfolioModal;
